<template>
  <div>
    <transition name="modal">
      <delete-modal
        v-if="showDeleteModal"
        :loading="loadingDelete"
        @close="closeDeleteModal()"
        @confirmDelete="deleteElement"
      ></delete-modal>
    </transition>

    <transition name="modal">
      <confirmation-modal
        v-if="confirmCurrentBudgetUpdate"
        :message="'¿Estás seguro que deseas actualizar el saldo actual?'"
        @close="confirmCurrentBudgetUpdate = false"
        @confirm="updateCurrentBudget"
      ></confirmation-modal>
    </transition>

    <transition name="modal">
      <confirmation-modal
        v-if="orderCycleToGenerateNewOrders"
        message="¿Estás seguro que deseas generar nuevas órdenes? Estas serán procesadas en cuanto se apruebe la solicitud."
        @close="orderCycleToGenerateNewOrders = null"
        @confirm="requestOrder(orderCycleToGenerateNewOrders)"
      ></confirmation-modal>
    </transition>

    <div v-if="!loading">
      <div v-if="quote" class="flex flex-col">
        <div class="card">
          <card-header :title="'Plan #' + quote.id">
            <template #badge>
              <status-badge :status="quote.status" />
            </template>
          </card-header>

          <div v-if="quote.status === 'canceled'" class="mb-4">
            <detail-field label="Fecha de cancelación" :value="formatDatetime(quote.canceled_at)" />
          </div>

          <section-header title="Información General" />

          <div class="detail-grid">
            <detail-field label="Cliente">
              <router-link
                :to="{ name: 'clientsDetail', params: { id: quote.client.id } }"
                class="detail-link"
                >{{ quote.client.name }} {{ quote.client.last_name }}</router-link
              >
            </detail-field>

            <detail-field label="Fecha de creación" :value="formatDatetime(quote.created_at)" />
            <detail-field label="Fecha de última actualización" :value="formatDatetime(quote.updated_at)" />
          </div>

          <div v-if="quote.status === 'pending'"></div>

          <div v-else>
            <section-header title="Detalles del Plan" />

            <div class="detail-grid">
              <detail-field label="Cantidad total por semana" :value="quote.total_quantity + 'g'" />

              <detail-field v-if="quote.payment_frequency" label="Frecuencia de pago" :value="quote.payment_frequency.name + ' - ' + quote.payment_frequency.weeks + ' ' + (quote.payment_frequency.weeks === 1 ? 'semana' : 'semanas')" />

              <detail-field label="Método de entrega">
                <div class="flex items-center justify-between gap-2">
                  <p class="detail-value">
                    {{ quote.current_pickup_delivery ? 'Recoger en sucursal' : 'Envío' }}
                  </p>
                  <button
                    v-if="hasPermission('edit_quotes') && ['active', 'paused'].includes(quote.status)"
                    type="button"
                    class="text-xs text-primary-600 underline hover:text-primary-800"
                    @click.prevent="openDeliveryModeModal"
                  >
                    Cambiar
                  </button>
                </div>
              </detail-field>

              <detail-field v-if="quote.delivery_frequency" label="Frecuencia de entrega" :value="quote.delivery_frequency.name + ' - ' + quote.delivery_frequency.weeks + ' ' + (quote.delivery_frequency.weeks === 1 ? 'semana' : 'semanas')" />

              <detail-field v-if="quote.discount_amount" label="Descuento" :value="integerToCurrency(quote.discount_amount)" />

              <detail-field v-if="quote.total_price" label="Total" :value="integerToCurrency(quote.total_price - quote.discount_amount)" :prominent="true" />

              <detail-field v-if="quote.current_payment_method_id" label="Método de pago" :value="quote.current_payment_method_id" />

              <detail-field v-if="quote.next_payment_date" label="Fecha del siguiente pago" :value="formatDate(quote.next_payment_date)" />
            </div>

            <section-header title="Dirección de envío" />
            <div v-if="quote.current_pickup_delivery" class="text-sm text-content-400">
              El cliente recoge en sucursal (Pick-Up).
            </div>
            <div v-else-if="quote.address" class="card-bordered w-fit text-sm">
              <p>{{ quote.address.street }} #{{ quote.address.exterior_number }}</p>
              <p v-if="quote.address.interior_number">Int. {{ quote.address.interior_number }}</p>
              <p v-if="quote.address.neighborhood">{{ quote.address.neighborhood }}</p>
              <p>{{ quote.address.city }}<span v-if="quote.address.state">, {{ quote.address.state.name }}</span></p>
              <p>C.P. {{ quote.address.zip_code }}</p>
              <p v-if="quote.address.references" class="text-content-400 mt-1">{{ quote.address.references }}</p>
            </div>
            <div v-else class="text-sm text-content-400">Sin dirección asignada.</div>

            <section-header title="Cupón de descuento" />
            <div v-if="quote.coupon" class="p-3 bg-primary-50 border border-primary-200 rounded-lg w-fit">
              <p class="font-semibold text-primary-700">{{ quote.coupon.code }}</p>
              <p class="text-sm text-content-400 mt-1">
                {{ couponDiscountLabel }}
                <span v-if="quote.coupon.first_purchase_only">· Solo primera compra</span>
              </p>
              <p v-if="quote.coupon_redemption" class="text-sm text-success-600 mt-1">
                Canjeado el {{ formatDate(quote.coupon_redemption.created_at) }}
                — descuento aplicado: {{ integerToCurrency(quote.coupon_redemption.discount_amount) }}
              </p>
              <p v-else class="text-sm text-content-400 mt-1">
                Pendiente de canje — se aplica en el primer pago.
              </p>
            </div>
            <div v-else class="text-sm text-content-400">Este plan no tiene cupón.</div>

            <p v-if="quote.order_cycles.length > 0" class="mt-4 font-medium">
              Órdenes del período actual
            </p>

            <div
              v-for="orderCycle in quote.order_cycles"
              :key="orderCycle.id"
              class="text-sm"
            >
              <div class="flex flex-wrap">
                <div
                  v-for="order in orderCycle.orders"
                  :key="order.id"
                  class="card-bordered mt-2 sm:mr-2"
                >
                  <p class="text-sm font-medium">
                    #{{ order.id }} · {{ order.diet && order.diet.pet ? order.diet.pet.name : "Sin mascota" }}
                  </p>
                  <p class="text-xs"><status-badge :status="order.status" /></p>
                  <p class="text-xs text-content-400 mt-1">
                    {{ order.weeks ? order.weeks + (order.weeks === 1 ? " semana" : " semanas") : "" }}
                    <span v-if="order.pickup_delivery !== null && order.pickup_delivery !== undefined">
                      · {{ order.pickup_delivery ? "Pick-Up" : "Envío" }}
                    </span>
                  </p>
                  <p v-if="order.delivery_date" class="text-xs text-content-400">
                    Entregada: {{ formatDate(order.delivery_date) }}
                  </p>
                </div>
              </div>

              <p
                v-if="orderCycle.total_cycles - orderCycle.cycle_count > 0"
                class="mt-4 font-medium"
              >
                Órdenes pendientes de generar en el período
              </p>

              <div class="flex flex-wrap">
                <div
                  v-for="index in Math.max(0, orderCycle.total_cycles - orderCycle.cycle_count)"
                  :key="index"
                  class="card-bordered mt-2 sm:mr-2"
                >
                  <div
                    v-for="diet in quote.diets.filter(
                      (diet) => diet.status === 'active'
                    )"
                    :key="diet.id"
                    class="mb-2 last:mb-0"
                  >
                    <p class="text-sm font-medium">{{ diet.pet.name }}</p>
                    <p class="text-xs text-content-400">
                      {{ diet.diet_stage?.display_name || 'Sin etapa' }} · {{ diet.diet_type?.display_name || 'Sin tipo' }}
                    </p>
                    <p class="text-xs text-content-400">
                      {{ diet.total_quantity }}g/semana · {{ integerToCurrency(diet.weekly_price) }}/semana
                    </p>
                    <router-link
                      :to="{ name: 'petsDetail', params: { id: diet.pet.id } }"
                      class="text-xs text-primary-500 hover:underline"
                    >
                      Ver dieta →
                    </router-link>
                  </div>
                </div>
              </div>

              <div
                v-if="orderCycle.total_cycles - orderCycle.cycle_count > 0"
                class="mt-2 text-sm"
              >
                <p class="font-medium">
                  Fecha de generación de
                  {{
                    quote.diets.length > 1
                      ? "siguiente set de órdenes"
                      : "siguiente orden"
                  }}
                </p>
                <p>
                  {{ formatDate(orderCycle.next_cycle_date) }}
                </p>

                <p v-if="hasPendingRequest(orderCycle)" class="mt-2">
                  Ya existe una solicitud pendiente.
                </p>
              </div>

              <!-- <div class="mt-4 text-sm">
                <p class="font-medium">Saldo Actual</p>

                <div v-if="showCurrentBudgetForm" class="flex flex-col">
                  <input
                    id="shipping_option"
                    type="number"
                    step="any"
                    v-model="currentBudget"
                    class="w-fit"
                  />

                  <button
                    class="cancel-button mt-2"
                    @click.prevent="showCurrentBudgetForm = false"
                  >
                    Cancelar
                  </button>

                  <primary-button
                    class="mx-0 mt-2"
                    :loading="loadingCurrentBudgetUpdate"
                    type="submit"
                    @click.prevent="confirmCurrentBudgetUpdate = true"
                    >Guardar</primary-button
                  >
                </div>
                <div v-else>
                  <p
                    :class="[
                      { 'text-success-600': orderCycle.current_budget > 0 },
                      { 'text-error-500': orderCycle.current_budget < 0 },
                    ]"
                  >
                    {{ integerToCurrency(orderCycle.current_budget) }}
                  </p>

                  <button
                    v-if="
                      !showCurrentBudgetForm &&
                      hasPermission('edit_quotes') &&
                      quote.status !== 'canceled'
                    "
                    class="secondary-button block mt-2"
                    @click.prevent="showCurrentBudgetForm = true"
                  >
                    Cambiar saldo actual
                  </button>
                </div>
              </div> -->
            </div>

            <section-header title="Pagos" />
            <p v-if="!quote.order_invoices.length" class="text-sm text-content-400">Aún no hay pagos registrados.</p>
            <div v-else class="flex flex-wrap gap-2">
              <div
                v-for="orderInvoice in quote.order_invoices"
                :key="orderInvoice.id"
                class="card-bordered w-full sm:w-72 h-fit"
              >
                <p class="font-medium">#{{ orderInvoice.id }}</p>
                <p class="text-sm font-medium">{{ integerToCurrency(orderInvoice.total) }}</p>
                <p class="text-xs text-content-400">{{ formatDatetime(orderInvoice.created_at) }}</p>
                <p v-if="orderInvoice.type" class="text-xs text-content-500 mt-1">{{ orderInvoice.type }}</p>
                <p class="text-xs text-content-500 mt-1">{{ invoiceMethod(orderInvoice) }}</p>
                <p v-if="Number(orderInvoice.coupon_discount) > 0" class="text-xs text-content-400">
                  Cupón: -{{ integerToCurrency(orderInvoice.coupon_discount) }}
                </p>
                <p v-if="Number(orderInvoice.wallet_applied) > 0" class="text-xs text-content-400">
                  Monedero: -{{ integerToCurrency(orderInvoice.wallet_applied) }}
                </p>
                <p v-if="Number(orderInvoice.refunded) > 0" class="text-sm font-medium mt-2 text-error-500">
                  {{ integerToCurrency(orderInvoice.refunded) }} reembolsados
                </p>
              </div>
            </div>

            <section-header title="Historial de pagos" />
            <p v-if="!quote.payment_attempts || !quote.payment_attempts.length" class="text-sm text-content-400">
              No hay intentos de pago registrados.
            </p>
            <div v-else class="table-wrapper">
              <table class="table-auto text-sm mt-2 w-full">
                <thead class="text-left">
                  <tr class="bg-gray-100">
                    <th class="p-2 rounded-l-lg">Fecha</th>
                    <th class="p-2">Tipo</th>
                    <th class="p-2">Monto</th>
                    <th class="p-2">Estado</th>
                    <th class="p-2">Método</th>
                    <th class="p-2">Origen</th>
                    <th class="p-2 rounded-r-lg">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="attempt in quote.payment_attempts" :key="attempt.id" class="table-row">
                    <td class="table-cell text-xs">{{ formatDatetime(attempt.created_at) }}</td>
                    <td class="table-cell text-xs">{{ attempt.type_label || attempt.type }}</td>
                    <td class="table-cell">{{ integerToCurrency(attempt.amount) }}</td>
                    <td class="table-cell"><status-badge :status="attempt.status" :label="attempt.status_label" /></td>
                    <td class="table-cell text-xs">
                      <span v-if="attempt.payment_method_brand">
                        {{ attempt.payment_method_brand.toUpperCase() }}
                        <span v-if="attempt.payment_method_last4">*{{ attempt.payment_method_last4 }}</span>
                      </span>
                      <span v-else class="text-content-400">—</span>
                    </td>
                    <td class="table-cell text-xs">
                      {{ attempt.triggered_by_label }}
                      <span v-if="attempt.triggered_by_admin" class="block text-content-400">
                        ({{ attempt.triggered_by_admin.name }} {{ attempt.triggered_by_admin.last_name }})
                      </span>
                    </td>
                    <td class="table-cell text-xs max-w-xs">
                      <span v-if="attempt.status === 'failed'" class="text-error-500">
                        {{ attempt.error_message_translated || attempt.error_message || "Error desconocido" }}
                      </span>
                      <span v-else class="text-content-400">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="quote.status === 'canceled'" class="mt-4 text-sm">
              <p class="font-medium">Razón de cancelación</p>
              <p>{{ quote.cancel_reason || "—" }}</p>
            </div>
          </div>
        </div>

        <div class="card mt-4">
          <section-header title="Dieta activa en el plan" />

          <p v-if="!activeDiets.length" class="text-sm text-content-400">Este plan no tiene dietas activas.</p>

          <div v-for="diet in activeDiets" :key="diet.id" class="mb-6 last:mb-0">
            <div class="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div class="flex items-center gap-2 flex-wrap">
                <p class="font-medium">{{ diet.pet ? diet.pet.name : "Mascota" }}</p>
                <status-badge v-if="diet.pet && diet.pet.deleted_at" status="canceled" label="Mascota eliminada" />
                <span class="text-xs text-content-400">
                  {{ diet.diet_stage?.display_name || "Sin etapa" }} · {{ diet.diet_type?.display_name || "Sin tipo" }}
                  · {{ diet.total_quantity }}g/semana · {{ integerToCurrency(diet.weekly_price) }}/semana
                </span>
              </div>
              <router-link
                v-if="diet.pet && !diet.pet.deleted_at"
                :to="{ name: 'petsDetail', params: { id: diet.pet.id } }"
                class="detail-link text-xs"
                >Ver mascota</router-link
              >
            </div>

            <diet :diet="diet" />

            <p class="mt-3 text-sm font-medium">Historial de cambios de dieta</p>
            <p v-if="!diet.diet_histories || !diet.diet_histories.length" class="text-xs text-content-400">
              Sin cambios registrados.
            </p>
            <table v-else class="table-auto text-sm mt-1 w-full">
              <thead class="text-left">
                <tr class="bg-gray-100">
                  <th class="p-2 rounded-l-lg">Evento</th>
                  <th class="p-2">Fecha</th>
                  <th class="p-2 rounded-r-lg">Dieta anterior</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="history in diet.diet_histories" :key="history.id" class="table-row">
                  <td class="table-cell">{{ history.event }}</td>
                  <td class="table-cell text-xs">{{ formatDatetime(history.created_at) }}</td>
                  <td class="table-cell text-xs text-content-400">{{ previousDietSummary(history) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="excludedDiets.length" class="mt-4">
            <p class="text-sm font-medium">Mascotas excluidas del plan</p>
            <p v-for="diet in excludedDiets" :key="'ex-' + diet.id" class="text-xs text-content-400">
              {{ diet.pet ? diet.pet.name : "Mascota" }} · {{ diet.name }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>

    <!-- Cambiar método de entrega modal -->
    <div
      v-if="showDeliveryModeModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30"
      @click.self="closeDeliveryModeModal"
    >
      <div class="bg-white rounded-lg shadow-xl p-6 max-w-md w-11/12">
        <h3 class="text-lg font-semibold mb-4">Cambiar método de entrega</h3>
        <p class="text-sm text-gray-600 mb-4">
          Aplica al plan en general. Los pedidos ya preparados no cambian; los pedidos pendientes o en proceso del ciclo actual sí se actualizan.
        </p>
        <div class="space-y-3">
          <label class="flex items-center space-x-2 cursor-pointer">
            <input type="radio" value="true" v-model="deliveryModeForm.pickupChoice" />
            <span class="text-sm">Recoger en sucursal (Pick-Up)</span>
          </label>
          <label class="flex items-center space-x-2 cursor-pointer">
            <input type="radio" value="false" v-model="deliveryModeForm.pickupChoice" />
            <span class="text-sm">Envío a domicilio</span>
          </label>
        </div>
        <div v-if="deliveryModeForm.pickupChoice === 'false'" class="mt-4">
          <label class="block text-sm font-medium mb-1">Dirección de envío</label>
          <select v-model="deliveryModeForm.addressId" class="w-full p-2 border rounded">
            <option :value="null" disabled>Selecciona una dirección</option>
            <option
              v-for="addr in (quote && quote.client ? quote.client.addresses : [])"
              :key="addr.id"
              :value="addr.id"
            >
              {{ addr.street }} #{{ addr.exterior_number }}, {{ addr.city }}
            </option>
          </select>
          <p v-if="!quote || !quote.client || !quote.client.addresses || quote.client.addresses.length === 0" class="text-xs text-red-600 mt-1">
            El cliente no tiene direcciones registradas.
          </p>
        </div>
        <div v-if="deliveryModeError" class="mt-3 text-sm text-red-600">
          {{ deliveryModeError }}
        </div>
        <div class="flex justify-end space-x-2 mt-6">
          <button
            type="button"
            class="secondary-button"
            :disabled="changingDeliveryMode"
            @click.prevent="closeDeliveryModeModal"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="primary-button"
            :disabled="changingDeliveryMode || !canSubmitDeliveryMode"
            @click.prevent="submitDeliveryModeChange"
          >
            {{ changingDeliveryMode ? "Guardando..." : "Guardar" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Diet from "@/components/models/Diet";
// import Payment from "@/components/models/Payment";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import api from "@/api";
import deleteMixin from "@/mixins/deleteMixin";
import validationErrors from "@/mixins/validationErrors";
import permissionsMixin from "@/mixins/permissionsMixin";
import { formatDate, formatDatetime } from "@/utils/dateFormatter";
import { integerToCurrency } from "@/utils/currencyFormatter";
export default {
  name: "Quote Detail",

  components: {
    Diet,
    // Payment,
    ConfirmationModal,
  },

  data() {
    return {
      quote: null,
      loading: false,
      loadingDelete: false,
      currentBudget: null,
      showCurrentBudgetForm: false,
      confirmCurrentBudgetUpdate: false,
      loadingCurrentBudgetUpdate: false,
      orderCycleToGenerateNewOrders: null,
      // Delivery-mode change modal
      showDeliveryModeModal: false,
      deliveryModeForm: { pickupChoice: 'true', addressId: null },
      changingDeliveryMode: false,
      deliveryModeError: null,
    };
  },

  mixins: [deleteMixin, validationErrors, permissionsMixin],

  computed: {
    activeDiets() {
      return (this.quote?.diets || []).filter((d) => d.status === "active");
    },

    excludedDiets() {
      return (this.quote?.diets || []).filter((d) => d.status === "excluded");
    },

    couponDiscountLabel() {
      const coupon = this.quote?.coupon;
      if (!coupon) return "";
      return coupon.discount_type === "percentage"
        ? `${Number(coupon.discount_value)}% de descuento`
        : `${this.integerToCurrency(coupon.discount_value)} de descuento`;
    },

    canSubmitDeliveryMode() {
      if (this.deliveryModeForm.pickupChoice === 'true') return true;
      return !!this.deliveryModeForm.addressId;
    },
  },

  created() {
    this.loadQuote();
  },

  methods: {
    formatDate,

    formatDatetime,

    integerToCurrency,

    openDeliveryModeModal() {
      this.deliveryModeError = null;
      this.deliveryModeForm = {
        pickupChoice: this.quote.current_pickup_delivery ? 'true' : 'false',
        addressId: this.quote.address_id || null,
      };
      this.showDeliveryModeModal = true;
    },

    closeDeliveryModeModal() {
      if (this.changingDeliveryMode) return;
      this.showDeliveryModeModal = false;
      this.deliveryModeError = null;
    },

    async submitDeliveryModeChange() {
      this.deliveryModeError = null;
      const wantPickup = this.deliveryModeForm.pickupChoice === 'true';
      if (!wantPickup && !this.deliveryModeForm.addressId) {
        this.deliveryModeError = 'Selecciona una dirección de envío.';
        return;
      }
      this.changingDeliveryMode = true;
      try {
        await api.quotes.changeDeliveryMode(this.quote.id, {
          pickup_delivery: wantPickup,
          address_id: wantPickup ? null : this.deliveryModeForm.addressId,
        });
        this.showDeliveryModeModal = false;
        this.$store.dispatch('successSnack', 'Método de entrega actualizado.');
        await this.loadQuote();
      } catch (e) {
        this.deliveryModeError =
          e?.response?.data?.message || 'No fue posible cambiar el método de entrega.';
      } finally {
        this.changingDeliveryMode = false;
      }
    },

    async loadQuote() {
      this.loading = true;

      try {
        this.quote = await api.quotes.getById(this.$route.params.id);
        this.quote.diets.forEach((diet) => (diet.collapsed = true));
        this.currentBudget = this.quote.order_cycles[0]
          ? this.quote.order_cycles[0].current_budget
          : null;
        this.$store.commit('setBreadcrumbLabel', {
          routeName: this.$route.name,
          label: 'Plan #' + this.quote.id,
        });
      } catch (error) {
        console.log(error);
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    getPaymentMethod(id) {
      const paymentMethod = this.paymentMethods.filter((p) => p.id === id)[0];

      return (
        "**** " +
        paymentMethod.card.last4 +
        " - " +
        paymentMethod.card.brand.toUpperCase() +
        " - " +
        paymentMethod.card.exp_month +
        "/" +
        paymentMethod.card.exp_year
      );
    },

    invoiceMethod(invoice) {
      if (invoice.payment_method === "cash") return "Efectivo";
      if (invoice.payment_method_brand && invoice.payment_method_last4) {
        return `${invoice.payment_method_brand.toUpperCase()} **** ${invoice.payment_method_last4}`;
      }
      return "Tarjeta";
    },

    previousDietSummary(history) {
      let previous = history.previous_state;
      if (typeof previous === "string") {
        try {
          previous = JSON.parse(previous);
        } catch (e) {
          return previous;
        }
      }
      if (!previous || typeof previous !== "object") return "—";
      const parts = [];
      if (previous.name) parts.push(previous.name);
      if (previous.diet_stage?.display_name) parts.push(previous.diet_stage.display_name);
      if (previous.diet_type?.display_name) parts.push(previous.diet_type.display_name);
      if (previous.total_quantity) parts.push(`${previous.total_quantity}g/semana`);
      if (previous.weekly_price) parts.push(`${this.integerToCurrency(previous.weekly_price)}/semana`);
      return parts.length ? parts.join(" · ") : "—";
    },

    getStatus(status) {
      switch (status) {
        case "pending":
          return "Pendiente";
        case "in_process":
          return "En Proceso";
        case "ready_for_pickup":
          return "Lista para pick-up";
        case "ready_for_delivery":
          return "Lista para entrega";
        case "sent":
          return "Enviada";
        case "delivered":
          return "Entregada";
        case "failed_to_send":
          return "Fallida al enviar";
        default:
          break;
      }
    },

    hasPendingRequest(orderCycle) {
      return (
        orderCycle.order_requests.filter(
          (request) => request.status === "pending"
        ).length > 0
      );
    },
  },
};
</script>